/**
 * chat-wait-sponsor.ts — ephemeral sponsorship slot for in-flight runs.
 *
 * Rendered by the thread shell as a *sibling* of the live activity strip,
 * never inside `chat-markdown` and never as a chat message. The slot has no
 * `data-msg-id`, is not part of the message store, and is excluded from
 * thread search, so a creative rotation can never re-render the virtualized
 * transcript or leak sponsor copy into copied text or the model prompt.
 *
 * Gate (see {@link shouldShowWaitSponsorSlot}):
 *   - a run is in flight (sending / abortable / streaming) OR a subagent
 *     row is still `running` / `spawning` / `queued`, AND
 *   - the assistant turn has not yet committed a final bubble, AND
 *   - the client is connected and a run id exists.
 *
 * Unmount paths are the ordinary run-terminal transitions: `chatRunId`
 * clears, `chat-turn--done` renders, abort succeeds (see
 * `clearChatWaitState` in chat-wait-ticker.ts), or the operator hits Stop.
 * Because the gate is derived from run state rather than from a timer, the
 * slot disappears on the same render tick as the run chrome.
 */

/** Container height reserved for the creative, so streaming tokens never reflow. */
export const WAIT_SPONSOR_SLOT_HEIGHT_PX = 96;

/** Creative rotation interval. Owned by the slot, not by the activity ticker. */
export const WAIT_SPONSOR_ROTATION_MS = 15_000;

/** Run statuses that count as "a subagent is still working". */
export const SPONSOR_ACTIVE_RUN_STATUSES = ["running", "spawning", "queued"] as const;

export type WaitSponsorCreative = {
  id: string;
  /** "Sponsored", "Ad", … — rendered as the disclosure label. */
  disclosure: string;
  headline: string;
  body: string;
  /** Optional inline SVG artwork. Kept decorative and `aria-hidden`. */
  artwork?: string;
};

/**
 * Sample creatives. Deliberately static for now: the slot must not fetch
 * at render time, or an offline gateway would leave a hole in the layout.
 */
export const WAIT_SPONSOR_CREATIVES: readonly WaitSponsorCreative[] = [
  {
    id: "warm-pool",
    disclosure: "Sponsored",
    headline: "Keep the run warm",
    body: "Reliable compute for long-running tasks. Warm pools, fast start, no cold boot.",
    artwork: "warm-pool",
  },
  {
    id: "context-window",
    disclosure: "Sponsored",
    headline: "Room for the whole thread",
    body: "Long-context storage that keeps your agent transcripts searchable.",
    artwork: "context-window",
  },
  {
    id: "observability",
    disclosure: "Ad",
    headline: "See what the agent is doing",
    body: "Step-level traces and cost attribution for every tool call.",
    artwork: "observability",
  },
];

/**
 * Decide whether the slot should be mounted.
 *
 * Returns false on error banners, when disconnected, and after abort —
 * `runId` and `turnCommitted` clear on every one of those paths.
 */
export function shouldShowWaitSponsorSlot(params: {
  connected: boolean;
  /** `host.chatRunId` — cleared by clearChatWaitState on abort / chat-final. */
  chatRunId: string | null | undefined;
  /** A parent chat.run is in flight. */
  runActive: boolean;
  /** Run statuses of the subagent rows currently on screen. */
  subagentRunStatuses: readonly string[];
  /** True once the assistant has committed its final `chat-turn--done` bubble. */
  turnCommitted: boolean;
  /** Suppressed while an error banner owns the slot area. */
  hasErrorBanner?: boolean;
}): boolean {
  if (params.hasErrorBanner) {
    return false;
  }
  if (!params.connected) {
    return false;
  }
  if (!params.chatRunId) {
    return false;
  }
  if (params.turnCommitted) {
    return false;
  }
  if (params.runActive) {
    return true;
  }
  // Parent already returned its first response but children are still
  // working — same reasoning as shouldShowLiveRunIndicator's
  // subagentLiveCount branch.
  return params.subagentRunStatuses.some((status) =>
    (SPONSOR_ACTIVE_RUN_STATUSES as readonly string[]).includes(status),
  );
}

/**
 * Pick the creative for a rotation index. Pure and time-seeded so the
 * first paint of a run is stable, then rotates on WAIT_SPONSOR_ROTATION_MS.
 */
export function resolveWaitSponsorCreative(
  rotationIndex: number,
  creatives: readonly WaitSponsorCreative[] = WAIT_SPONSOR_CREATIVES,
): WaitSponsorCreative | null {
  if (creatives.length === 0) {
    return null;
  }
  const index =
    ((Math.trunc(rotationIndex) % creatives.length) + creatives.length) % creatives.length;
  return creatives[index] ?? null;
}

/**
 * Impression record. Ephemeral by construction: it lives on the run object
 * only and is never pushed into the message store or persisted.
 */
export type WaitSponsorImpression = {
  runId: string;
  slot: "wait-sponsor";
  creativeId: string;
  shownAt: number;
};

export function buildWaitSponsorImpression(params: {
  runId: string;
  creativeId: string;
  nowMs?: number;
}): WaitSponsorImpression {
  return {
    runId: params.runId,
    slot: "wait-sponsor",
    creativeId: params.creativeId,
    shownAt: params.nowMs ?? Date.now(),
  };
}
